import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, InputNumber, message, Typography, Space, Spin, Alert, Divider, Card, Form } from 'antd';
import { CopyOutlined, LinkOutlined, MailOutlined, MessageOutlined } from '@ant-design/icons';
import { paymentService } from '../../services/paymentService';
import { getBaseUrl } from '../../utils/getBaseUrl';
import { copyToClipboard } from '../../utils/clipboard';
import axios from 'axios';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface PaymentLinkModalProps {
  visible: boolean;
  onCancel: () => void;
  entityType: 'estimate' | 'work_order';
  entityId: number;
  entityNumber: string;
  customerId: number;
  customerEmail: string;
  customerPhone: string;
  customerName: string;
  amount: number;
  description?: string;
  vehicleInfo?: string;
}

/**
 * PaymentLinkModal - Generate and display Stripe payment link
 * Allows owner to send payment link to customer
 */
const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  visible,
  onCancel,
  entityType,
  entityId,
  entityNumber,
  customerId,
  customerEmail,
  customerPhone,
  customerName,
  amount,
  description,
  vehicleInfo,
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [sendToEmail, setSendToEmail] = useState(customerEmail);
  const [paymentAmount, setPaymentAmount] = useState<number>(amount);

  useEffect(() => {
    if (visible) {
      setSendToEmail(customerEmail);
      setPaymentAmount(amount); // Reset to balance_due when modal opens
      setPaymentUrl(null); // Clear previous link when modal opens
    }
  }, [visible, customerEmail, amount]);

  const handleAmountChange = (value: number | null) => {
    if (value !== null && value > 0) {
      // Don't allow payment amount to exceed balance due
      const maxAmount = Math.min(value, amount);
      setPaymentAmount(maxAmount);
      // Clear existing payment URL when amount changes
      setPaymentUrl(null);
    }
  };

  const generatePaymentLink = async () => {
    if (paymentAmount <= 0) {
      message.error('Payment amount must be greater than $0');
      return;
    }

    if (paymentAmount > amount) {
      message.error('Payment amount cannot exceed balance due');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const successUrl = `${baseUrl}/payment/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/payment/canceled`;

      const result = await paymentService.createCheckoutSession({
        amount: paymentAmount,
        entityType,
        entityId,
        customerId,
        customerEmail,
        customerName,
        description: description || `Payment for ${entityType === 'work_order' ? 'Work Order' : 'Estimate'} #${entityId}`,
        successUrl,
        cancelUrl,
      });

      setPaymentUrl(result.url);
    } catch (err: any) {
      console.error('Error generating payment link:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate payment link');
      message.error('Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (paymentUrl) {
      const success = await copyToClipboard(paymentUrl);
      if (success) {
        message.success('Payment link copied to clipboard!');
      } else {
        message.error('Failed to copy link');
      }
    }
  };

  const handleOpenLink = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  const handleSendEmail = async () => {
    if (!paymentUrl) {
      message.error('Please generate payment link first');
      return;
    }

    if (!sendToEmail || !sendToEmail.trim()) {
      message.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sendToEmail)) {
      message.error('Please enter a valid email address');
      return;
    }

    setEmailSending(true);

    try {
      // Get company info from shop settings
      const shopSettingsResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/shop-settings`, {
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem('cnf_auth_tokens') || '{}').accessToken}`
        }
      });

      const companyInfo = {
        name: shopSettingsResponse.data.company_name || 'CNF Auto',
        address: shopSettingsResponse.data.company_address || '',
        city: '',
        state: '',
        zip: '',
        phone: shopSettingsResponse.data.company_phone || '',
        email: shopSettingsResponse.data.company_email || '',
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/email/send-payment-link`,
        {
          customerName,
          customerEmail: sendToEmail,
          paymentLink: paymentUrl,
          amount: paymentAmount,
          entityType,
          entityNumber,
          vehicleInfo,
          customMessage: customMessage || undefined,
          companyInfo
        },
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('cnf_auth_tokens') || '{}').accessToken}`
          }
        }
      );

      message.success(`Payment link sent to ${sendToEmail}`);
      setCustomMessage('');
    } catch (err: any) {
      console.error('Error sending payment link email:', err);
      message.error(err.response?.data?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleClose = () => {
    setPaymentUrl(null);
    setError(null);
    setCustomMessage('');
    setSendToEmail(customerEmail);
    onCancel();
  };

  return (
    <Modal
      title="Payment Link"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
      ]}
      width={600}
      centered
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Paragraph>
            Generate a secure payment link to send to the customer. They can click the link to pay online using Stripe.
          </Paragraph>
        </div>

        {/* Payment Amount Input */}
        <Card style={{ marginBottom: 16 }} size="small">
          <Form layout="inline" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Form.Item label="Payment Amount" style={{ marginBottom: 0, flex: 1 }}>
              <InputNumber
                value={paymentAmount}
                onChange={handleAmountChange}
                min={0.01}
                max={amount}
                precision={2}
                prefix="$"
                style={{ width: '100%' }}
                size="large"
                disabled={loading || !!paymentUrl}
              />
            </Form.Item>
            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
              Balance Due: ${amount.toFixed(2)}
            </div>
          </Form>
          <Alert
            message="Split Payment Enabled"
            description="You can enter any amount up to the balance due. The customer can make partial payments, and the remaining balance can be paid later."
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
          {paymentUrl && (
            <Alert
              message="Link Generated"
              description="To generate a new link with a different amount, close this modal and open it again."
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
          {!paymentUrl && (
            <Button
              type="primary"
              onClick={generatePaymentLink}
              loading={loading}
              block
              size="large"
              style={{ marginTop: 12 }}
            >
              Generate Payment Link
            </Button>
          )}
        </Card>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Generating payment link...</Text>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={generatePaymentLink}>
                Retry
              </Button>
            }
          />
        )}

        {paymentUrl && !loading && (
          <>
            {!customerEmail && !customerPhone && (
              <Alert
                message="Missing Contact Information"
                description="This customer has no email or phone number on file. Please enter an email address below to send the payment link, or copy the link to share manually."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <div>
              <Text strong>Payment Details:</Text>
              <div style={{ marginTop: 8 }}>
                <Text>Customer: {customerName}</Text>
                <br />
                <Text>Link Amount: ${paymentAmount.toFixed(2)}</Text>
                {paymentAmount < amount && (
                  <>
                    <br />
                    <Text type="secondary">Remaining Balance: ${(amount - paymentAmount).toFixed(2)}</Text>
                  </>
                )}
                {customerPhone && (
                  <>
                    <br />
                    <Text>Phone: {customerPhone}</Text>
                  </>
                )}
              </div>
            </div>

            <div>
              <Text strong>Payment Link:</Text>
              <Input.Group compact style={{ marginTop: 8 }}>
                <Input
                  value={paymentUrl}
                  readOnly
                  style={{ width: 'calc(100% - 140px)' }}
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyLink}
                  type="primary"
                >
                  Copy Link
                </Button>
              </Input.Group>
            </div>

            <Divider>Send Payment Link</Divider>

            {/* Email Section */}
            <Card
              title={
                <Space>
                  <MailOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Send via Email</Text>
                </Space>
              }
              size="small"
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong style={{ fontSize: 12 }}>Email Address:</Text>
                  <Input
                    placeholder={!customerEmail ? "Enter customer's email address" : "Enter email address"}
                    value={sendToEmail}
                    onChange={(e) => setSendToEmail(e.target.value)}
                    prefix={<MailOutlined />}
                    style={{ marginTop: 4 }}
                    disabled={emailSending}
                    status={!sendToEmail && !customerEmail ? 'warning' : undefined}
                  />
                  {!customerEmail && !sendToEmail && (
                    <Text type="warning" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                      ⚠️ No email on file - please enter an email address to send payment link
                    </Text>
                  )}
                  {sendToEmail !== customerEmail && customerEmail && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                      Changed from customer's email: {customerEmail}
                    </Text>
                  )}
                </div>
                <div>
                  <Text strong style={{ fontSize: 12 }}>Custom Message (Optional):</Text>
                  <TextArea
                    rows={3}
                    placeholder="Add a custom message to include in the email..."
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    style={{ marginTop: 4 }}
                    disabled={emailSending}
                  />
                </div>
                <Button
                  icon={<MailOutlined />}
                  onClick={handleSendEmail}
                  loading={emailSending}
                  block
                  type="primary"
                  size="large"
                >
                  Send Payment Link via Email
                </Button>
              </Space>
            </Card>

            {/* SMS Section - Coming Soon */}
            <Card
              title={
                <Space>
                  <MessageOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Send via SMS</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>(Coming Soon)</Text>
                </Space>
              }
              size="small"
              style={{ opacity: 0.6 }}
            >
              <div style={{ padding: '8px 0' }}>
                {customerPhone ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ fontSize: 12 }}>Phone Number:</Text>
                      <div style={{ marginTop: 4, padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <Text>{customerPhone}</Text>
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      SMS payment link functionality will be available soon. You can currently send payment links via email or copy the link to share manually via text.
                    </Text>
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    No phone number on file. SMS payment link functionality will be available soon. You can currently send payment links via email or copy the link to share manually.
                  </Text>
                )}
              </div>
            </Card>

            <div>
              <Space>
                <Button
                  icon={<LinkOutlined />}
                  onClick={handleOpenLink}
                >
                  Preview Link
                </Button>
              </Space>
            </div>

            <Alert
              message="How to Use"
              description={
                <>
                  <div><strong>Option 1: Send via Email (Recommended)</strong></div>
                  <div>• Enter or edit the customer's email address</div>
                  <div>• Add a custom message (optional)</div>
                  <div>• Click "Send Payment Link via Email"</div>
                  <div>• Customer receives a professional email with a payment button</div>
                  <br />
                  <div><strong>Option 2: Manual Sharing</strong></div>
                  <div>• Copy the payment link above</div>
                  <div>• Share it via text message, WhatsApp, or other platforms</div>
                  <br />
                  <div>💡 The link expires in 24 hours and payment status updates automatically</div>
                </>
              }
              type="info"
              showIcon
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

export default PaymentLinkModal;
